import React, { useMemo, useRef, useState } from 'react'
import { useNewTranslation } from 'hooks/index'
import { UnmetRequirement } from 'services/api/registrations'
import { REQUIREMENT_TYPE_ENUM } from 'state/campaigns'
import { PLATFORM_ENUM } from 'services/api/constants'
import Text from 'particles/Text'
import { isOverflown } from 'particles/utils'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import Avatar from 'particles/Avatar'
import styled from 'particles/themes/styled'
import Tooltip from 'particles/Tooltip'
import { PartiposterProfile } from 'state/submissions'
import { TFunction } from 'i18next'

export interface ProfileDataProps {
    profile: PartiposterProfile
    hasRequirements: boolean
    unmetRequirement: UnmetRequirement[]
    displayExpirationBadge?: boolean
}

interface GridCellProps {
    dataProps: ProfileDataProps
    width?: number
}

const defaultStaticPhoto = 'https://static.partipost.com/images/default-photo.png'

const getFollowerType = (id: PLATFORM_ENUM, t: TFunction): string | undefined => {
    switch (id) {
        case PLATFORM_ENUM.IG:
            return t('socialMedia.requirement.instagramFollowers')
        case PLATFORM_ENUM.FB:
            return t('socialMedia.requirement.facebookFriends')
        case PLATFORM_ENUM.TIKTOK:
            return t('socialMedia.requirement.tiktokFollowers')
        case PLATFORM_ENUM.TWITTER:
            return t('socialMedia.requirement.twitterFollowers')
        default:
            return
    }
}
const getType = (unmet: UnmetRequirement, t: TFunction): string | undefined => {
    switch (unmet.id) {
        case REQUIREMENT_TYPE_ENUM.AGE:
            return t('campaignEditor.requirement.ageHeader', 'Age')
        case REQUIREMENT_TYPE_ENUM.GENDER:
            return t('campaignEditor.requirement.genderHeader', 'Gender')
        case REQUIREMENT_TYPE_ENUM.FOLLOWER:
            return unmet.platform ? getFollowerType(unmet.platform, t) : undefined
        case REQUIREMENT_TYPE_ENUM.OCCUPATION:
            return t('campaignEditor.requirement.occupationHeader', 'Occupation')
        case REQUIREMENT_TYPE_ENUM.LOCATION:
            return t('campaignEditor.requirement.locationHeader', 'Location')
        default:
            return
    }
}

const GridCellExpand = React.memo(function GridCellExpand({ dataProps, width }: GridCellProps) {
    const [t] = useNewTranslation()
    const { unmetRequirement, profile, hasRequirements, displayExpirationBadge } = dataProps

    const nameRef = useRef<HTMLDivElement | null>(null)
    const ageRef = useRef<HTMLDivElement | null>(null)
    const badgeRef = useRef<HTMLDivElement | null>(null)
    const [showNameTooltip, setShowNameTooltip] = useState(false)
    const [showAgeTooltip, setShowAgeTooltip] = useState(false)
    const [showBadgeTooltip, setShowBadgeTooltip] = useState(false)

    const isMet = unmetRequirement?.length === 0
    const requirements = useMemo(() => {
        return isMet
            ? ''
            : unmetRequirement
                  ?.map(unmet => getType(unmet, t))
                  .filter(item => item)
                  .join(', ')
    }, [unmetRequirement])

    const handleNameMouseEnter = () => {
        const isNameOverflown = isOverflown(nameRef.current!)
        setShowNameTooltip(isNameOverflown)
    }

    const handleNameMouseLeave = () => {
        setShowNameTooltip(false)
    }

    const handleAgeMouseEnter = () => {
        const isAgeOverflown = isOverflown(ageRef.current!)
        setShowAgeTooltip(isAgeOverflown)
    }

    const handleAgeMouseLeave = () => {
        setShowAgeTooltip(false)
    }

    const handleBadgeMouseEnter = () => {
        const isBadgeOverflown = isOverflown(badgeRef.current!)
        setShowBadgeTooltip(isBadgeOverflown)
    }

    const handleBadgeMouseLeave = () => {
        setShowBadgeTooltip(false)
    }

    return (
        <StyledProfileContainer>
            <div className="cellValue">
                <div className="top-header">
                    <div className="name-container">
                        <Tooltip label={profile?.name} placement="top" open={showNameTooltip}>
                            <Avatar
                                profile={profile}
                                size="m"
                                className="avatar"
                                imageToIgnore={defaultStaticPhoto}
                                onMouseEnter={handleNameMouseEnter}
                                onMouseLeave={handleNameMouseLeave}
                            />
                        </Tooltip>
                        <Tooltip label={profile?.name} placement="top" open={showNameTooltip}>
                            <Text
                                ref={nameRef}
                                scale="bodyS500"
                                className="name"
                                onMouseEnter={handleNameMouseEnter}
                                onMouseLeave={handleNameMouseLeave}
                            >
                                {profile?.name}
                            </Text>
                        </Tooltip>
                    </div>
                    {hasRequirements && (
                        <Tooltip
                            placement="top"
                            title={
                                isMet ? (
                                    <Text scale="caption700" color="primaryInverse">
                                        {t('submission.table.meetRequirementsLabel')}
                                    </Text>
                                ) : (
                                    <>
                                        <Text scale="caption700" color="primaryInverse">
                                            {t('submission.table.notMeetRequirementsLabel')}
                                        </Text>
                                        <Text scale="caption400" color="primaryInverse">
                                            {requirements}
                                        </Text>
                                    </>
                                )
                            }
                        >
                            <Text>{isMet ? '✅' : '❌'}</Text>
                        </Tooltip>
                    )}
                </div>

                <Tooltip
                    label={t('submission.table.ageDetails', { Age: profile.age })}
                    placement="top"
                    open={showAgeTooltip}
                >
                    <Text
                        ref={ageRef}
                        scale="bodyS500"
                        className="age-and-gender"
                        onMouseEnter={handleAgeMouseEnter}
                        onMouseLeave={handleAgeMouseLeave}
                    >
                        {`${profile.gender}, ${t('submission.table.ageDetails', { Age: profile.age })}`}
                    </Text>
                </Tooltip>
            </div>
            {displayExpirationBadge && (
                <Tooltip label={t('tag.registrationExpired')} placement="top" open={showBadgeTooltip}>
                    <div className="badge">
                        <Text
                            ref={badgeRef}
                            scale="caption400"
                            color="error"
                            className="badge-text"
                            onMouseEnter={handleBadgeMouseEnter}
                            onMouseLeave={handleBadgeMouseLeave}
                        >
                            {t('tag.registrationExpired')}
                        </Text>
                    </div>
                </Tooltip>
            )}
        </StyledProfileContainer>
    )
})

export const ProfileCell = (params: GridRenderCellParams) => {
    const dataProps: ProfileDataProps = params.value
    return <GridCellExpand dataProps={dataProps} width={params?.colDef?.width} />
}

const StyledProfileContainer = styled.div`
    display: flex;
    position: relative;
    flex-direction: column;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing[100]};
    width: 100%;
    height: 100%;

    .cellValue {
        width: 100%;
        min-width: 100%;
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[100]};
    }

    .top-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: ${({ theme }) => theme.spacing[200]};
    }

    .name-container {
        display: flex;
        align-items: center;
        width: 100%;
        overflow: hidden;
        gap: ${({ theme }) => theme.spacing[200]};
    }

    .avatar div {
        color: ${({ theme }) => theme.colors.text.primaryInverse} !important;
    }

    .name {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    .age-and-gender {
        width: 100%;
        text-overflow: ellipsis;
        white-space: nowrap;
        word-break: break-word;
        overflow: hidden;
    }

    .badge {
        padding: 0 6px;
        max-width: fit-content;
        height: 18px;
        border-radius: 30px;
        background: #ffe7e9;
        box-sizing: border-box;
    }

    .badge-text {
        width: 100%;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }
`
